import { create } from 'zustand'
import { getEnv } from '@/lib/utils'
import { gatewayFetch } from '@/lib/gateway-auth'

interface LlmModel {
  name: string
  modified_at: string
  size: number
  digest: string
  details?: {
    format?: string
    family?: string
    families?: string[]
    parameter_size?: string
    quantization_level?: string
  }
}

interface LlmState {
  models: LlmModel[]
  currentModel: string
  isLoading: boolean
  error: string | null

  setCurrentModel: (model: string) => void
  fetchModels: () => Promise<void>
}

const MODEL_STORAGE_KEY = 'omtae.llm.currentModel'

const readStoredModel = () => {
  if (typeof window === 'undefined') return ''
  return window.localStorage.getItem(MODEL_STORAGE_KEY) || ''
}

const persistModel = (model: string) => {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(MODEL_STORAGE_KEY, model)
}

function getLlmProxyUrl(): string {
  const gw = getEnv('VITE_GATEWAY_URL').replace(/\/$/, '')
  return gw ? `${gw}/llm` : '/api/llm'
}

function getGatewayUrl(): string {
  const gw = getEnv('VITE_GATEWAY_URL').replace(/\/$/, '')
  return gw || '/api'
}

const getDefaultModel = () => {
  return readStoredModel() || getEnv('VITE_LLM_MODEL') || 'Qwen2.5-Coder-32B-Instruct-AWQ'
}

const getDefaultEmbeddingModel = () => {
  return getEnv('VITE_LLM_EMBED_MODEL') || 'bge-large'
}

export const useLlmStore = create<LlmState>((set, get) => ({
  models: [],
  currentModel: getDefaultModel(),
  isLoading: false,
  error: null,

  setCurrentModel: (model: string) => {
    persistModel(model)
    set({ currentModel: model })
  },

  fetchModels: async () => {
    set({ isLoading: true, error: null })

    let models: LlmModel[] = []
    let lastError: string | null = null

    // Try vLLM proxy first (shows running models)
    try {
      const base = getLlmProxyUrl()
      const response = await gatewayFetch(`${base}/v1/models`)
      if (response.ok) {
        const data = (await response.json()) as { data?: Array<{ id: string }> }
        models = (data.data || []).map((entry) => ({
          name: entry.id,
          modified_at: '',
          size: 0,
          digest: '',
        }))
      }
    } catch (err) {
      lastError = `LLM unreachable: ${(err as Error).message}`
    }

    // Always fetch from gateway /models (all downloaded models) and merge
    try {
      const gwBase = getGatewayUrl()
      const response = await gatewayFetch(`${gwBase}/models`)
      if (response.ok) {
        const data = (await response.json()) as { models?: Array<{ id: string; name: string; status: string; capabilities?: { toolCalling?: boolean; agentic?: boolean; toolParser?: string } }> }
        const managed = data.models || []
        const existingNames = new Set(models.map((m) => m.name))
        for (const m of managed) {
          if (!existingNames.has(m.name) && (m.status === 'running' || m.status === 'downloaded')) {
            models.push({
              name: m.name,
              modified_at: '',
              size: 0,
              digest: '',
              details: m.capabilities ? {
                format: m.capabilities.agentic ? 'agentic' : m.capabilities.toolCalling ? 'tool-calling' : 'chat',
                family: m.capabilities.toolParser || undefined,
              } : undefined,
            })
          }
        }
      }
    } catch (err) {
      if (!lastError) lastError = `Gateway unreachable: ${(err as Error).message}`
    }

    if (models.length === 0 && lastError) {
      set({ models: [], isLoading: false, error: lastError })
      return
    }

    const state = get()
    const hasCurrentModel = models.some((m) => m.name === state.currentModel)
    if (!hasCurrentModel && models.length > 0) {
      const fallbackModel = models[0].name
      persistModel(fallbackModel)
      set({ models, currentModel: fallbackModel, isLoading: false, error: null })
    } else {
      set({ models, isLoading: false, error: null })
    }
  },
}))

export const llmOpenAI = {
  async chatCompletions(
    messages: Array<{ role: string; content: string }>,
    options: {
      model?: string
      temperature?: number
      max_tokens?: number
      top_p?: number
      stream?: boolean
      signal?: AbortSignal
    } = {},
  ) {
    const baseUrl = getLlmProxyUrl()
    const model = options.model || getDefaultModel()

    const response = await gatewayFetch(`${baseUrl}/v1/chat/completions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: options.signal,
      body: JSON.stringify({
        model,
        messages,
        temperature: options.temperature ?? 0.7,
        max_tokens: options.max_tokens ?? 4096,
        top_p: options.top_p ?? 0.9,
        stream: options.stream ?? false,
      }),
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Chat completion failed: ${error}`)
    }

    return response.json()
  },

  async listModels() {
    const baseUrl = getLlmProxyUrl()
    const response = await gatewayFetch(`${baseUrl}/v1/models`)
    if (!response.ok) throw new Error('Failed to list models')
    return response.json()
  },

  async embeddings(input: string | string[], options: { model?: string } = {}) {
    const baseUrl = getLlmProxyUrl()
    const model = options.model || getDefaultEmbeddingModel()

    const response = await gatewayFetch(`${baseUrl}/v1/embeddings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model, input }),
    })

    if (!response.ok) throw new Error('Embeddings failed')

    return response.json()
  },
}
